/** Pet Guard client (§24) — profiles, safe zones, location, lost mode, walks. */
import { api } from './client';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

export type Species = 'dog' | 'cat' | 'other';

export interface Pet {
  id: string;
  family_id: string;
  name: string;
  photo_url: string | null;
  species: Species;
  breed: string | null;
  age_years: number | null;
  weight_kg: number | null;
  color: string | null;
  microchip_id: string | null;
  medical_notes: string | null;
  vet_contact: string | null;
  emergency_contact: string | null;
  lost_active: boolean;
  lost_message: string | null;
  lost_started_at: string | null;
  tag_token: string;
  created_at: string;
}

export interface PetCreate {
  name: string;
  species: Species;
  breed?: string;
  age_years?: number;
  weight_kg?: number;
  color?: string;
  microchip_id?: string;
  medical_notes?: string;
  vet_contact?: string;
  emergency_contact?: string;
}

export interface PetSafeZone {
  id: string;
  pet_id: string;
  name: string;
  center_lat: number;
  center_lng: number;
  radius_m: number;
  icon: string | null;
  last_state: string;
}

export interface PetPosition {
  pet_id: string;
  lat: number;
  lng: number;
  battery_pct: number | null;
  accuracy_m: number | null;
  reported_at: string;
  freshness_seconds: number;
}

export interface PetWalk {
  id: string;
  pet_id: string;
  child_id: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  distance_m: number;
}

export interface PetScan {
  id: string;
  pet_id: string;
  message: string | null;
  finder_contact: string | null;
  approx_lat: number | null;
  approx_lng: number | null;
  read_at: string | null;
  scanned_at: string;
}

export const petsApi = {
  list: () => api<Pet[]>('/v1/pets'),
  create: (body: PetCreate) => api<Pet>('/v1/pets', { method: 'POST', body }),
  update: (id: string, body: Partial<PetCreate>) => api<Pet>(`/v1/pets/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<void>(`/v1/pets/${id}`, { method: 'DELETE' }),
  tag: (id: string) => api<{ token: string; url: string }>(`/v1/pets/${id}/tag`),
  qrUrl: (token: string) => `${API_BASE}/v1/pets/public/${token}/qr.svg`,
  positions: () => api<PetPosition[]>('/v1/pets/positions'),
  zones: (id: string) => api<PetSafeZone[]>(`/v1/pets/${id}/safe-zones`),
  addZone: (id: string, body: { name: string; center_lat: number; center_lng: number; radius_m: number }) =>
    api<PetSafeZone>(`/v1/pets/${id}/safe-zones`, { method: 'POST', body }),
  removeZone: (id: string, zoneId: string) => api<void>(`/v1/pets/${id}/safe-zones/${zoneId}`, { method: 'DELETE' }),
  activateLost: (id: string, message?: string) =>
    api<Pet>(`/v1/pets/${id}/lost`, { method: 'POST', body: { message: message ?? null } }),
  deactivateLost: (id: string) => api<Pet>(`/v1/pets/${id}/lost`, { method: 'DELETE' }),
  walks: (id: string) => api<PetWalk[]>(`/v1/pets/${id}/walks`),
  startWalk: (id: string, childId?: string) =>
    api<PetWalk>(`/v1/pets/${id}/walks`, { method: 'POST', body: { child_id: childId ?? null } }),
  finishWalk: (id: string, walkId: string) =>
    api<PetWalk>(`/v1/pets/${id}/walks/${walkId}/finish`, { method: 'POST', body: { points: [] } }),
  scans: () => api<PetScan[]>('/v1/pets/scans'),
  markScanRead: (id: string) => api<PetScan>(`/v1/pets/scans/${id}/read`, { method: 'POST' }),
  uploadPhoto: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api<Pet>(`/v1/pets/${id}/photo`, { method: 'POST', body: fd });
  },
  deletePhoto: (id: string) => api<Pet>(`/v1/pets/${id}/photo`, { method: 'DELETE' }),
};
